// import 'regenerator-runtime/runtime';

import ExcelJS from 'exceljs';

interface ColumnOptions {
  numFmt?: string;
}
type TRow = object;

const getMaxContentWidth = (col: Partial<ExcelJS.Column>) =>
  Math.max(
    col.header?.length ?? 0,
    // exceljs types this as a single CellValue, but it's actually an array of column values
    ...((col.values as unknown as ExcelJS.CellValue[] | undefined)
      ?.map((val) => val?.toString()?.length ?? 0)
      .filter(Boolean) ?? []),
  );

const addSheetColumnsAndRows = (sheet: ExcelJS.Worksheet, rows: TRow[]) => {
  sheet.columns = Object.keys(rows[0]).map((header, index) => {
    const key = index.toString();
    return {
      header,
      key,
    };
  });

  sheet.addRows(
    rows.map((row) =>
      Object.keys(row).reduce((rowData, header, index) => {
        const key = index.toString();
        return {
          ...rowData,
          [key]: (row as any)[header],
        };
      }, {}),
    ),
  );
};

const handleColumnOptions = (
  sheet: ExcelJS.Worksheet,
  columnFormats?: Partial<Record<keyof TRow, ColumnOptions>>,
) => {
  if (columnFormats) {
    (Object.entries(columnFormats) as [string, ColumnOptions][]).forEach(
      ([header, format]) => {
        const column = sheet.columns.find((col) => col.header === header);
        if (!column) {
          return;
        }
        column.eachCell?.((cell, rowNumber) => {
          // skip header
          if (rowNumber === 1) {
            return;
          }
          if (format.numFmt) {
            cell.numFmt = format.numFmt;
          }
        });
      },
    );
  }
};

export const addSheetToExcelWorkbook = (
  workbook: ExcelJS.Workbook,
  title: string,
  rows: TRow[],
  columnFormats?: Partial<Record<keyof TRow, ColumnOptions>>,
) => {
  if (!rows.length) {
    return;
  }

  const sheet = workbook.addWorksheet(title);

  addSheetColumnsAndRows(sheet, rows);

  sheet.columns.forEach((col) => {
    col.width = getMaxContentWidth(col) * 1.1; // visual margin
  });

  sheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true };
  });

  handleColumnOptions(sheet, columnFormats);
};
export const getExcelExport = async <TRow extends object>(
  sheets: {
    title: string;
    rows: TRow[];
    columnOptions?: Partial<Record<keyof TRow, ColumnOptions>>;
  }[],
): Promise<Blob> => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Shell Battery Management Hub';
  workbook.created = new Date();

  sheets.forEach(({ title, rows, columnOptions }) =>
    addSheetToExcelWorkbook(workbook, title, rows, columnOptions),
  );

  const buffer = await workbook.csv.writeBuffer({
    encoding: 'utf8',
    formatterOptions: {
      delimiter: ',',
      quote: true,
      writeBOM: true,
    },
  });

  return new Blob([buffer], { type: 'text/csv;charset=utf-8' });
};
